// React, Next
import { useContext } from 'react'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import Link from 'next/link'

// MUI
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import Breadcrumbs from '@mui/material/Breadcrumbs'
import MuiLink from '@mui/material/Link'
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'

// Custom
import Section from '@/components/sections/Section'
import { translateHref } from '@/assets/functions/links'
import { GlobalContext } from 'context/GlobalContextProvider'

// Img
import HeroImage from 'public/img/services/ot_awareness_training.webp'

// Config
import nextI18nextConfig from 'next-i18next.config'

// Style
import themeColors from 'assets/theme/colors'
import breakpoints from 'assets/theme/breakpoints'
import { getFontBody } from '@/assets/theme/fonts'

/**
 * The wash laid over the hero photo, in the same off-white the alternating
 * section bands use, so the hero and the section below it agree.
 */
const wash = (alpha: number) =>
  `rgba(${themeColors.section.lightRgb}, ${alpha})`

export default function HeroSection() {
  const { t } = useTranslation('servicesOtAwarenessTraining')
  const { screenDimensions } = useContext(GlobalContext)

  const router = useRouter()
  const currentLocale =
    router.query.locale || nextI18nextConfig.i18n.defaultLocale

  const w = screenDimensions.width
  const isDesktop = w > breakpoints.desktopToMobile
  const isTablet = w > breakpoints.mobileLarge && !isDesktop

  /**
   * The photo is untouched - this wash is what makes the copy legible. On
   * narrow screens the text sits over the whole image, so it runs vertically
   * and stays close to opaque.
   */
  const getOverlay = () => {
    if (isDesktop)
      return `linear-gradient(to right, ${wash(0.97)} 0%, ${wash(0.93)} 32%, ${wash(0.6)} 62%, ${wash(0.3)} 100%)`
    if (isTablet)
      return `linear-gradient(to right, ${wash(0.97)} 0%, ${wash(0.9)} 55%, ${wash(0.6)} 100%)`
    return `linear-gradient(to bottom, ${wash(0.95)} 0%, ${wash(0.92)} 60%, ${wash(0.85)} 100%)`
  }

  const getMinHeight = () => {
    if (w > breakpoints.desktopSmall) return '38rem'
    if (isDesktop) return '34rem'
    if (isTablet) return '30rem'
    return 'auto'
  }

  const getTextWidth = () => {
    if (w > breakpoints.desktopSmall) return '52%'
    if (isDesktop) return '62%'
    if (isTablet) return '78%'
    return '100%'
  }

  const getTitleFont = () => {
    if (w > breakpoints.desktopSmall) return '4rem'
    if (isDesktop) return '3.25rem'
    if (isTablet) return '2.75rem'
    if (w > breakpoints.mobileSmall) return '2.25rem'
    return '1.875rem'
  }

  const getSubtitleFont = () => {
    if (isDesktop) return '1.5rem'
    if (isTablet) return '1.25rem'
    return '1.125rem'
  }

  const getContent = () => (
    <Box sx={{ width: '100%' }}>
      <Breadcrumbs
        aria-label="breadcrumb"
        sx={{
          paddingTop: '75px',
          marginBottom: isDesktop ? '2.5rem' : '1.5rem'
        }}
      >
        <MuiLink
          underline="hover"
          color="inherit"
          href={translateHref(currentLocale, 'services')}
        >
          {t('crumbs.parent')}
        </MuiLink>
        <Typography sx={{ color: themeColors.success.darker }}>
          {t('crumbs.current')}
        </Typography>
      </Breadcrumbs>

      <Box sx={{ width: getTextWidth() }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: getTitleFont(),
            fontWeight: 600,
            lineHeight: 1.1,
            letterSpacing: '-0.02em',
            color: themeColors.kroemtech.dunkelblau,
            marginBottom: '1.5rem'
          }}
        >
          {t('hero.title')}
        </Typography>

        <Typography
          component="p"
          sx={{
            fontSize: getSubtitleFont(),
            fontWeight: 600,
            lineHeight: 1.35,
            color: themeColors.kroemtech.dunkelblau,
            marginBottom: '1.5rem'
          }}
        >
          {t('hero.subtitle')}
        </Typography>

        {[
          t('hero.description1'),
          t('hero.description2'),
          t('hero.description3')
        ].map(paragraph => (
          <Typography
            key={paragraph}
            component="p"
            sx={{
              fontSize: getFontBody(),
              lineHeight: 1.7,
              color: themeColors.gray.defaultText,
              marginBottom: '0.9rem',
              maxWidth: '34rem',
              '&:last-of-type': { marginBottom: '1.75rem' }
            }}
          >
            {paragraph}
          </Typography>
        ))}

        <Typography
          component="p"
          sx={{
            fontSize: getFontBody(),
            fontWeight: 500,
            color: themeColors.bg.start,
            marginBottom: '2rem'
          }}
        >
          {t('hero.footer')}
        </Typography>

        <Link href={translateHref(currentLocale, 'contact')}>
          <Button
            variant="contained"
            endIcon={<ArrowForwardIosIcon sx={{ transform: 'scale(0.7)' }} />}
            sx={{
              background: themeColors.success.main,
              borderRadius: '4px',
              padding: '0.75rem 1.5rem',
              fontWeight: 600,
              letterSpacing: '0.03em',
              boxShadow: 'none',
              '&:hover': {
                background: themeColors.success.darker,
                boxShadow: 'none'
              }
            }}
          >
            {t('hero.cta')}
          </Button>
        </Link>
      </Box>
    </Box>
  )

  // *** MAIN RENDER ***
  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        minHeight: getMinHeight(),
        display: 'flex',
        alignItems: 'center',
        backgroundImage: `url(${HeroImage.src})`,
        backgroundSize: 'cover',
        backgroundPosition: isDesktop ? 'center' : 'right center',
        backgroundRepeat: 'no-repeat',
        paddingBottom: isDesktop ? '3rem' : '2.5rem'
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background: getOverlay(),
          pointerEvents: 'none'
        }}
      />

      <Box sx={{ position: 'relative', width: '100%', zIndex: 1 }}>
        <Section
          justifyContent="left"
          background="transparent"
          content={getContent()}
        />
      </Box>
    </Box>
  )
}
